// Y-axis display range for a matrix-family chart, ported from the coffee block (getDisplayRange).
// The exporters ask for it so a static image keeps the axis the researcher is looking at; the
// rendered chart answers first, and this maths is the fallback for a chart that has not drawn yet.
//
// Only the three averaging views have an axis worth reporting -- a stacked chart is drawn against
// its own hundred percent, and an area chart against the ranking positions. Each of the three reads
// its sub-questions in a measure of its own, error bars included, and each has a natural scale it
// falls back to when the researcher zooms out.

import { round } from '@aytm/app/stats/lib/util/helpers'
import { getErrData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/errData'

const NON_RANGE_CHART_MODES = ['map', 'none']

export const RANGE_VIEW_MODES = ['score', 'meanScore', 'boxScore']

// A mean is plotted as a single point, so its error bars are the distance from that point to the
// bounds the payload names; a box score names both bounds outright.
const meanErrorsOf = (mean, amount, confidenceLevels) =>
  ['errLO', 'errHI'].reduce((acc, err) => {
    if (!mean?.[err]) return acc

    return { ...acc, [err]: confidenceLevels.map((level, index) => [Math.abs(amount - mean[err][index]) || 0]) }
  }, {})

const boxErrorsOf = (box, confidenceLevels) =>
  ['errLO', 'errHI'].reduce((acc, err) => {
    if (!box?.[err]) return acc

    return { ...acc, [err]: confidenceLevels.map((level, index) => [box[err][index][0] || 0, box[err][index][1] || 0]) }
  }, {})

// How far the sub-questions reach in the measure the view reads them in.
const calcReach = ({
  answer,
  batches,
  viewMode,
  topsMode,
  mode,
  percentsMode,
  ignoreAmount,
  ignorePercents,
  rType,
  confidenceLevels
}) =>
  (batches || []).reduce(
    (reach, batch) => {
      const value = answer[`b${batch?.position}`] || {}

      if ('meanScore' === viewMode) {
        const amount = value.mean?.value || 0
        const { minErrValue, maxErrValue } = getErrData(
          { amount, total: value.total || answer.total, ...meanErrorsOf(value.mean, amount, confidenceLevels) },
          'amount',
          { ignorePercents: true, rType: 'cases', answerType: 'amount', confidenceLevels }
        )

        return {
          min: Math.min(reach.min, amount - (minErrValue || 0)),
          max: Math.max(reach.max, amount + (maxErrValue || 0))
        }
      }

      if ('boxScore' === viewMode) {
        const box = value.box?.[topsMode]
        const plotted = parseFloat((percentsMode ? box?.percents : box?.amount) || 0)

        const { minErrValue, maxErrValue } = getErrData(
          {
            amount: box?.amount || 0,
            percents: box?.percents || 0,
            total: value.total || answer.total,
            ...boxErrorsOf(box, confidenceLevels)
          },
          mode,
          { ignoreAmount, ignorePercents, rType, answerType: 'amount', confidenceLevels }
        )

        return {
          min: Math.min(reach.min, plotted - (minErrValue || 0)),
          max: Math.max(reach.max, plotted + (maxErrValue || 0))
        }
      }

      const score = Object.hasOwn(value, 'scale') ? value.scale.amount : value.score / value.total || 0

      return { min: Math.min(reach.min, score), max: Math.max(reach.max, score) }
    },
    { min: 0, max: 0 }
  )

// The axis the sub-questions are drawn against: rounded outwards, and standing at a hundred points
// for a question whose sub-questions all came out at nothing.
const calcBounds = ({ min, max }) => {
  const low = 0 > min ? -Math.ceil(Math.abs(min)) : 0

  if (0 >= max) return { low, high: 0 === low ? 100 : Math.ceil(Math.abs(low)) }

  return { low, high: Math.ceil(max) }
}

// The whole scale the view is read against, which is what zooming out shows: a score runs the
// question's own degrees, a mean starts at one of them, and a box score is a share of a hundred.
const calcFullScale = ({ viewMode, degree, answer, low, high }) => {
  if ('score' === viewMode && degree && !answer?.hasNegativeScores) return [0, degree]
  if ('meanScore' === viewMode && degree) return [1, degree]
  if ('boxScore' === viewMode) return [0, 100]

  return low !== high ? [round(low, 2), round(high, 2)] : null
}

export const calcDisplayRange = ({
  chartMode,
  viewMode,
  topsMode,
  zoomLevel,
  globalChartMode,
  format = {},
  answer = {},
  batches = [],
  degree,
  rType = 'cases',
  ignoreAmount: ignoreAmountFlag,
  ignorePercents: ignorePercentsFlag,
  confidenceLevels = []
} = {}) => {
  if (NON_RANGE_CHART_MODES.includes(chartMode) || NON_RANGE_CHART_MODES.includes(globalChartMode)) return null

  if (!RANGE_VIEW_MODES.includes(viewMode)) return null

  if (!['min', 'max'].includes(zoomLevel)) return zoomLevel ?? null

  const percentsMode = String(chartMode ?? '').endsWith('percents')

  const { low, high } = calcBounds(
    calcReach({
      answer,
      batches,
      viewMode,
      topsMode,
      mode: percentsMode ? 'percents' : 'amount',
      percentsMode,
      ignoreAmount: true === format?.percentsOnly || ignoreAmountFlag || false,
      ignorePercents: true === format?.amountOnly || ignorePercentsFlag || false,
      rType,
      confidenceLevels
    })
  )

  if ('max' === zoomLevel) return calcFullScale({ viewMode, degree, answer, low, high })

  return [low, high]
}
